const prefix = '_SPO_';
export enum Storages {
  local,
  cookie,
  session,
  memory,
}

const cookie = {
  getItem: (name: string): string | null =>
    (document?.cookie || '').split(';').reduce(
      (acc: Record<string, string | null>, curr: string) => ({
        ...acc,
        [curr.split('=')[0].trim()]: curr.split('=')[1]?.trim() || null,
      }),
      {},
    )?.[name] || null,
  setItem: (name: string, value: unknown) => {
    document.cookie = `${name}=${JSON.stringify(value)}`;
  },
  removeItem: (name: string) => {
    document.cookie = `${name}=;`;
  },
};

const _memory: Record<string, unknown> = {};
const memory = {
  getItem: <T>(name: string): T => {
    return (_memory?.[name] || '') as T;
  },
  setItem: (name: string, value: unknown) => {
    _memory[name] = value;
  },
  removeItem: (name: string) => {
    delete _memory[name];
  },
};

const checkStorage = (requestedStorage: Storages) => {
  switch (requestedStorage) {
    case Storages.cookie:
      if (document?.cookie) return Storages.cookie;
      else if (localStorage?.getItem !== null) return Storages.local;
      else return Storages.memory;
    case Storages.local:
      if (localStorage?.getItem !== null) return Storages.local;
      else if (document?.cookie) return Storages.cookie;
      else return Storages.memory;
    case Storages.session:
      if (sessionStorage?.getItem !== null) return Storages.session;
      else return Storages.memory;
    default:
      return Storages.memory;
  }
};

export const storageHelper = {
  getItem: <T>(name: string, storage: Storages = Storages.local): T | null => {
    const _name = `${prefix}${name}`;
    const availableStorage = checkStorage(storage);
    let storedData: string | null;
    switch (availableStorage) {
      case Storages.cookie:
      case Storages.local:
        storedData = (
          availableStorage === Storages.cookie ? cookie : localStorage
        )?.getItem(_name);
        if (!storedData) return null;
        try {
          return JSON.parse(storedData) as T;
        } catch (err) {
          // Do nothing
        }
        return storedData as T;
      case Storages.session:
        storedData = sessionStorage?.getItem(_name);
        if (!storedData) return null;
        return storedData as T;
      default:
        storedData = memory?.getItem(_name);
        if (!storedData) return null;
        return storedData as T;
    }
  },

  setItem: (
    name: string,
    value: unknown,
    storage: Storages = Storages.local,
  ) => {
    const _name = `${prefix}${name}`;
    const availableStorage = checkStorage(storage);
    switch (availableStorage) {
      case Storages.cookie:
        cookie?.setItem(_name, value);
        break;
      case Storages.local:
        window.localStorage?.setItem(
          _name,
          ['string', 'boolean', 'number'].includes(typeof value)
            ? // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              value!.toString()
            : JSON.stringify(value),
        );
        break;
      case Storages.session:
        sessionStorage.setItem(
          _name,
          ['string', 'boolean', 'number'].includes(typeof value)
            ? // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              value!.toString()
            : JSON.stringify(value),
        );
        break;
      default:
        memory?.setItem(_name, value);
        break;
    }
  },

  /**
   * Remove all the session storage items that match the given name pattern
   *
   *
   * @param namePattern - The keys pattern you want to delete from the session storage
   * @example storageHelper.removeItemsFromSession('personal-offer_my-view'); will remove all the session items that starts with `${prefix}personal-offer_my-view`
   */
  removeItemsFromSession: (namePattern: string) => {
    const allSessionKeys = Object.keys(sessionStorage);
    allSessionKeys.forEach((name) => {
      if (name.includes(namePattern)) {
        const key = name.split(`${prefix}`)[1];
        storageHelper.removeItem(key, Storages.session);
      }
    });
  },

  removeItem: (name: string, storage: Storages = Storages.local) => {
    const _name = `${prefix}${name}`;
    const availableStorage = checkStorage(storage);
    switch (availableStorage) {
      case Storages.cookie:
        cookie?.removeItem(_name);
        break;
      case Storages.local:
        localStorage?.removeItem(_name);
        break;
      case Storages.session:
        sessionStorage?.removeItem(_name);
        break;
      default:
        memory?.removeItem(_name);
        break;
    }
  },
};
